import { useMutation } from '@tanstack/react-query';
import { CadSearchsaveResource, UnitStatusSearchForm } from '@mark43/cad-api';
import { createApiErrorHandler, useApiClient } from '@m43/core';
import { HStack, useToast, VStack, Box, Button, Text } from '@cad-arc';
import { errorOccurredWhile } from '../common/helpers/error-occurred-while';
import { useUnitStatusSearchSortContext } from './unit-status-search-sort-provider';
import { SortMenu } from '../search-windows/sort-menu';
import { useState } from 'react';
import { UnitStatusSearchResultsAppliedFilters } from './unit-status-search-results-applied-filters';

export function UnitStatusSearchResultsHeader({
    hasResults,
    unitStatusSearchForm,
    savedSearchId,
    setSavedSearchId,
}: {
    hasResults: boolean | undefined;
    unitStatusSearchForm: UnitStatusSearchForm;
    savedSearchId?: number;
    setSavedSearchId: (savedSearchId?: number) => void;
}) {
    const sort = useUnitStatusSearchSortContext();
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const apiClient = useApiClient();
    const toast = useToast();
    const saveSearchMutation = useMutation((unitStatusSearchForm: UnitStatusSearchForm) => {
        return apiClient
            .request<CadSearchsaveResource.SaveUnitStatusSearch>({
                method: 'POST',
                url: 'cad_search/save/unit_status',
                data: { unitStatusSearchForm },
            })
            .then((resp) => {
                const unitStatusSaveResponse = resp.data.data;
                if (unitStatusSaveResponse) {
                    setSavedSearchId(unitStatusSaveResponse.savedUnitStatusSearchDto.savedSearchId);
                }
            })
            .catch(
                createApiErrorHandler(() => {
                    toast({ status: 'error', description: errorOccurredWhile('saving search') });
                })
            );
    });
    const unsaveSearchMutation = useMutation((savedSearchId: number) => {
        return apiClient
            .request<CadSearchsaveResource.DeleteSavedUnitStatusSearch>({
                method: 'POST',
                url: 'cad_search/save/unit_status/delete',
                data: { savedSearchId },
            })
            .then(() => {
                setSavedSearchId(undefined);
            })
            .catch(
                createApiErrorHandler(() => {
                    toast({ status: 'error', description: errorOccurredWhile('unsaving search') });
                })
            );
    });
    return (
        <VStack w="100%">
            <VStack
                w="100%"
                backgroundColor="surface.canvas"
                align="left"
                padding="24px 56px 12px 56px"
            >
                <HStack justifyContent="space-between">
                    <Text variant="headingMd">Unit Status Search Results</Text>
                    <div />
                </HStack>
                <HStack justifyContent="space-between">
                    <SortMenu
                        isDisabled={!hasResults}
                        sortBy={{
                            value: sort.sortByValue,
                            onChange: (value) => sort.setSortByValue(value),
                            options: sort.sortByOptions,
                        }}
                        direction={{
                            value: sort.directionValue,
                            onChange: (value) => sort.setDirectionValue(value),
                            options: sort.directionOptions,
                        }}
                    />
                    <Button
                        variant="ghost"
                        leadingVisual={showFilters ? 'Close' : 'Open'}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        {`${showFilters ? 'Hide' : 'Show'} Applied Filters`}
                    </Button>
                    {savedSearchId ? (
                        <Button
                            variant="ghost"
                            leadingVisual="SavedSearchActive"
                            onClick={() => unsaveSearchMutation.mutate(savedSearchId)}
                            isLoading={unsaveSearchMutation.isLoading}
                        >
                            Unsave Search
                        </Button>
                    ) : (
                        <Button
                            variant="ghost"
                            leadingVisual="SavedSearchInactive"
                            onClick={() => saveSearchMutation.mutate(unitStatusSearchForm)}
                            isLoading={saveSearchMutation.isLoading}
                        >
                            Save Search
                        </Button>
                    )}
                </HStack>
            </VStack>
            {showFilters && <UnitStatusSearchResultsAppliedFilters form={unitStatusSearchForm} />}
            {/* for spacing */}
            <Box />
        </VStack>
    );
}
